import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Pause, Play, Trash2, Copy, Unlock, Receipt, Archive, AlertTriangle, X, ShoppingCart, Gavel, KeyRound, Landmark, Printer, QrCode } from "lucide-react";
import { InfoHint } from "./InfoHint";
import DealerLogoInlinePrompt from "../dealer/DealerLogoInlinePrompt";

/**
 * Inserat-Aktionen (Edit-Form). Feb-2026 (User-Wunsch):
 *  - Alle Verkaufs-/Status-Aktionen sind hier gebündelt: „Verkaufen / Reservieren",
 *    „Als Auktion anbieten", „Zur Vermietung", Pausieren, Löschen, Kopieren.
 *  - „Verkaufen/Reservieren" + die Konvertierungs-Buttons öffnen den vollständigen
 *    Workflow in der Disposition (DispositionActions) — sie klappen die Disposition
 *    automatisch auf und stoßen die jeweilige Aktion an (Single Source of Truth).
 *  - „Löschen" prüft serverseitig Reservierung/Ankauf/Rechnungen. Ist eines davon
 *    vorhanden, ist nur eine Archivierung (mit Begründung) möglich.
 *  - Archivierte Inserate bleiben in der Suche sichtbar (Badge „Archivierung"),
 *    können aber nicht erneut online gestellt werden.
 *  - „Kopieren" übernimmt dieselben Stammdaten wie eine Vorlage (FIN, EZ, KM,
 *    Zustand, Vorbesitzer, Preise, Bilder, Dispo werden NICHT übernommen).
 */
export default function ListingActionButtons({ listingId, currentStatus, soldFlag = false, saleType, sharingEnabled, listingLabel, onAfterChange, onDuplicated }) {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(null);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiveReason, setArchiveReason] = useState("");
    const [archiveReasons, setArchiveReasons] = useState([]);
    // Feb-2026 (User-Wunsch) — Pfand / Refinanzieren
    const [pledgeOpen, setPledgeOpen] = useState(false);
    const [pledge, setPledge] = useState({ active: true, kind: "pfand", partner: "", amount: "", date: "", note: "" });
    const [pledgeActive, setPledgeActive] = useState(false);
    const isSoldOrReserved = currentStatus === "sold" || currentStatus === "reserved";
    const isSold = currentStatus === "sold" || soldFlag;
    const isArchived = currentStatus === "archived";
    const isPaused = currentStatus === "paused";

    // Feb-2026 (User-Wunsch) — Proxy in die Disposition: den passenden
    // Verkaufs-/Konvertierungs-Button anstoßen. Jul-2026 (User-Wunsch): dabei
    // die Sektion „VERKAUFSABWICKLUNG" aufklappen (dort geht es nach dem
    // Kunden-Popup weiter) und „Disposition" einklappen.
    const triggerDispoAction = (testId) => {
        const dispoToggle = document.querySelector('[data-testid="disposition-section-toggle"]');
        if (dispoToggle) {
            const expanded = dispoToggle.querySelector('[aria-expanded="true"]');
            if (expanded) { try { expanded.click(); } catch { /* noop */ } }
        }
        const toggle = document.querySelector('[data-testid="verkaufsabwicklung-section-toggle"]');
        if (toggle) {
            const collapsed = toggle.querySelector('[aria-expanded="false"]');
            if (collapsed) { try { collapsed.click(); } catch { /* noop */ } }
        }
        const tryClick = (attempt = 0) => {
            const btn = document.querySelector(`[data-testid="${testId}"]`);
            if (btn) {
                try { btn.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ }
                btn.click();
                return;
            }
            if (attempt < 14) setTimeout(() => tryClick(attempt + 1), 150);
            else toast.error("Verkaufsabwicklung konnte nicht geöffnet werden — bitte den Abschnitt manuell aufklappen.");
        };
        setTimeout(() => tryClick(), 250);
    };

    const run = async (action, label, fn) => {
        if (busy) return;
        setBusy(action);
        try {
            const data = await fn();
            toast.success(label);
            if (onAfterChange) await onAfterChange(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || `${label} fehlgeschlagen`);
        } finally {
            setBusy(null);
        }
    };

    const onRelease = () => {
        if (!window.confirm('Fahrzeug wirklich wieder freigeben?\n\nDer Status springt zurück auf "aktiv", Käufer-Stammdaten (Vorname, Name, E-Mail, Handy) werden gelöscht.')) return;
        return run("release", "Fahrzeug freigegeben — Inserat wieder aktiv.", async () => {
            const { data } = await api.post(`/listings/${listingId}/release`);
            return data;
        });
    };

    const onCreateInvoice = () => run("invoice", "§25a-Rechnung als Entwurf angelegt", async () => {
        const { data } = await api.post(`/invoices/from-listing/${listingId}`);
        setTimeout(() => navigate("/dealer/invoices"), 600);
        return data;
    });

    const onTogglePause = () => {
        if (isPaused) {
            return run("activate", "Inserat aktiviert — wieder öffentlich sichtbar", async () => {
                const { data } = await api.post(`/listings/${listingId}/activate`);
                return data;
            });
        }
        return run("pause", "Inserat pausiert — bis zur Aktivierung nicht öffentlich sichtbar", async () => {
            const { data } = await api.post(`/listings/${listingId}/pause`);
            return data;
        });
    };

    // Löschen: bei 409 (archive_required) öffnet sich der Archivierungs-Dialog.
    const onDelete = async () => {
        if (busy) return;
        if (!window.confirm("Inserat wirklich löschen?\n\nDie Protokolldaten werden zur Abrechnung 2 Jahre aufbewahrt, das Inserat selbst ist sofort nicht mehr öffentlich sichtbar.")) return;
        setBusy("delete");
        try {
            const { data } = await api.delete(`/listings/${listingId}`);
            toast.success("Inserat gelöscht (Protokoll bleibt 2 Jahre)");
            if (onAfterChange) await onAfterChange(data);
        } catch (err) {
            const d = err.response?.data?.detail;
            if (err.response?.status === 409 && d && typeof d === "object" && d.code === "archive_required") {
                setArchiveReasons(d.reasons || []);
                setArchiveReason("");
                setArchiveOpen(true);
            } else {
                toast.error(formatApiErrorDetail(d) || "Löschen fehlgeschlagen");
            }
        } finally {
            setBusy(null);
        }
    };

    const submitArchive = async () => {
        if (!archiveReason.trim()) { toast.error("Bitte einen Archivierungsgrund angeben."); return; }
        setBusy("archive");
        try {
            const { data } = await api.post(`/listings/${listingId}/archive`, { reason: archiveReason.trim() });
            toast.success("Inserat archiviert — bleibt in der Suche sichtbar, kann aber nicht erneut online gestellt werden.");
            setArchiveOpen(false);
            if (onAfterChange) await onAfterChange(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Archivierung fehlgeschlagen");
        } finally {
            setBusy(null);
        }
    };

    // Nov-2026 (User-Wunsch) — endgültig löschen TROTZ Verknüpfungen
    // (nur Filialleitung/Hauptsitz/Admin; Backend prüft die Berechtigung).
    const submitForceDelete = async () => {
        if (!window.confirm("Fahrzeug WIRKLICH endgültig entfernen — trotz Verknüpfungen (Reservierung/Verkauf/Rechnungen)?\n\nDie Protokolldaten bleiben 2 Jahre erhalten.")) return;
        setBusy("force-delete");
        try {
            const { data } = await api.delete(`/listings/${listingId}?force=1`);
            toast.success("Fahrzeug endgültig entfernt (Protokoll bleibt 2 Jahre)");
            setArchiveOpen(false);
            if (onAfterChange) await onAfterChange(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Endgültiges Löschen fehlgeschlagen");
        } finally {
            setBusy(null);
        }
    };

    const onCopy = () => run("copy", "Inserat kopiert — wie eine Vorlage (FIN, EZ, KM, Zustand, Vorbesitzer, Preise, Bilder, Dispo zurückgesetzt).", async () => {
        const { data } = await api.post(`/listings/${listingId}/duplicate`);
        if (onDuplicated) onDuplicated(data.id);
        return data;
    });

    // Feb-2026 (User-Wunsch) — Pfand / Refinanzieren: Pfand-Status beim Mount laden
    // (zur Einfärbung des Buttons) + Modal-Handling.
    useEffect(() => {
        let alive = true;
        api.get(`/listings/${listingId}/dispo/pledge`)
            .then((r) => { if (alive) setPledgeActive(Boolean(r.data?.active && (r.data?.partner || r.data?.amount))); })
            .catch(() => {});
        return () => { alive = false; };
    }, [listingId]);

    const openPledge = async () => {
        try {
            const { data } = await api.get(`/listings/${listingId}/dispo/pledge`);
            if (data && (data.partner || data.amount || data.kind)) {
                setPledge({
                    active: data.active !== false,
                    kind: data.kind || "pfand",
                    partner: data.partner || "",
                    amount: data.amount || "",
                    date: data.date || "",
                    note: data.note || "",
                });
            }
        } catch { /* leer starten */ }
        setPledgeOpen(true);
    };

    const savePledge = async () => {
        setBusy("pledge");
        try {
            const { data } = await api.patch(`/listings/${listingId}/dispo/pledge`, {
                active: pledge.active,
                kind: pledge.kind,
                partner: pledge.partner.trim(),
                amount: Number(pledge.amount) || 0,
                date: pledge.date,
                note: pledge.note.trim(),
            });
            setPledgeActive(Boolean(data.pledge?.active && (data.pledge?.partner || data.pledge?.amount)));
            toast.success(pledge.kind === "refinanzierung" ? "Refinanzierung gespeichert" : "Pfand gespeichert");
            setPledgeOpen(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="listing-action-buttons">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Aktionen</div>
                    <InfoHint
                        id="listing-actions-hint"
                        side="bottom"
                        defaultText={`„Verkaufen / Reservieren“, „Als Auktion anbieten“ und „Zur Vermietung“ öffnen den vollständigen Workflow in der Disposition. Löschen ist nur möglich, wenn keine Reservierung, kein Ankauf und keine Rechnungen vorhanden sind — sonst nur Archivierung. Beim Kopieren werden (wie bei Vorlagen) nur die Stammdaten übernommen.`}
                    />
                </div>
                {isArchived && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-gray-800 text-white px-2.5 py-1 rounded-sm" data-testid="listing-archived-badge">
                        <Archive className="w-3.5 h-3.5" /> Archiviert
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {/* Jun-2026 (User-Wunsch) — Inline-Logo-Upload für Händler ohne Profil-Logo */}
                {!isArchived && (
                    <div className="col-span-2 md:col-span-4"><DealerLogoInlinePrompt /></div>
                )}
                {/* Juni-2026 (User-Wunsch) — Preisschild-Druck links vom „Verkaufen / Reservieren" */}
                {!isArchived && (
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/listings/${listingId}/price-tag.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold uppercase tracking-wider bg-white border-2 border-[#111827] text-[#111827] hover:bg-gray-50"
                        data-testid="listing-action-pricetag"
                        title="Druckbares A5-Preisschild mit Händlerlogo, Kontaktdaten & QR-Code öffnen"
                    >
                        <Printer className="w-4 h-4 shrink-0" />
                        <span className="text-sm leading-tight text-left normal-case font-bold">Preisschild<br />drucken</span>
                    </a>
                )}
                {/* Jun-2026 (User-Wunsch) — „QR-Code drucken" als eigener Button neben dem Preisschild.
                    Jul-2026: beide Aktionen zweizeilig & besser lesbar (User-Wunsch). */}
                {!isArchived && (
                    <a
                        href={`/listing/${listingId}/qrcard`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold uppercase tracking-wider bg-white border-2 border-[#111827] text-[#111827] hover:bg-gray-50"
                        data-testid="listing-action-qrcard"
                        title="Druckbare QR-Karten (Schlüssel · Vorne Links · Hinten Rechts) öffnen"
                    >
                        <QrCode className="w-4 h-4 shrink-0" />
                        <span className="text-sm leading-tight text-left normal-case font-bold">QR-Code<br />drucken</span>
                    </a>
                )}
                {!isArchived && !isSoldOrReserved && (
                    <button
                        type="button"
                        onClick={() => triggerDispoAction("dispo-btn-primary-sell")}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="listing-action-sell"
                        title="Öffnet den Verkaufs-/Reservierungs-Workflow in der Disposition."
                    >
                        <ShoppingCart className="w-4 h-4" /> Verkaufen / Reservieren
                    </button>
                )}
                {!isArchived && !isSoldOrReserved && saleType !== "auction" && (
                    <button
                        type="button"
                        onClick={() => triggerDispoAction("dispo-btn-quick-auction")}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-[#111827] hover:bg-black text-[#FFCC00]"
                        data-testid="listing-action-auction"
                        title="Wandelt das Inserat in eine Auktion um (Disposition)."
                    >
                        <Gavel className="w-4 h-4" /> Als Auktion anbieten
                    </button>
                )}
                {!isArchived && !isSoldOrReserved && !sharingEnabled && (
                    <button
                        type="button"
                        onClick={() => triggerDispoAction("dispo-btn-quick-rental")}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
                        data-testid="listing-action-rental"
                        title="Verschiebt das Inserat in die Vermietung (Disposition)."
                    >
                        <KeyRound className="w-4 h-4" /> Zur Vermietung
                    </button>
                )}
                {!isArchived && !isSold && (
                    <button
                        type="button"
                        onClick={openPledge}
                        className={`flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider text-white ${pledgeActive ? "bg-violet-700 hover:bg-violet-800" : "bg-violet-500 hover:bg-violet-600"}`}
                        data-testid="listing-action-pledge"
                        title="Fahrzeug verpfänden oder über eine Bank refinanzieren."
                    >
                        <Landmark className="w-4 h-4" /> {pledgeActive ? "Pfand / Refinanz. ✓" : "Pfand / Refinanzieren"}
                    </button>
                )}
                {isSoldOrReserved && (
                    <button
                        type="button"
                        onClick={onRelease}
                        disabled={busy === "release"}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60"
                        data-testid="listing-action-release"
                        title="Setzt den Inserat-Status von verkauft/reserviert zurück auf aktiv und löscht die Käufer-Stammdaten."
                    >
                        <Unlock className="w-4 h-4" /> Fahrzeug freigeben
                    </button>
                )}
                {isSold && (
                    <button
                        type="button"
                        onClick={onCreateInvoice}
                        disabled={busy === "invoice"}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] disabled:opacity-60"
                        data-testid="listing-action-invoice"
                        title="Erstellt eine §25a-konforme Rechnung mit den hinterlegten Käuferdaten."
                    >
                        <Receipt className="w-4 h-4" /> §25a-Rechnung
                    </button>
                )}
                {!isArchived && (
                    <button
                        type="button"
                        onClick={onTogglePause}
                        disabled={busy === "pause" || busy === "activate"}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-60"
                        data-testid="listing-action-pause-toggle"
                    >
                        {isPaused ? (<><Play className="w-4 h-4" /> Aktivieren</>) : (<><Pause className="w-4 h-4" /> Pausieren</>)}
                    </button>
                )}
                {!isArchived && (
                    <button
                        type="button"
                        onClick={onDelete}
                        disabled={busy === "delete"}
                        className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
                        data-testid="listing-action-delete"
                    >
                        <Trash2 className="w-4 h-4" /> Löschen
                    </button>
                )}
                <button
                    type="button"
                    onClick={onCopy}
                    disabled={busy === "copy"}
                    className="flex items-center justify-center gap-2 h-12 rounded-sm font-bold text-sm uppercase tracking-wider bg-gray-500 hover:bg-gray-600 text-white disabled:opacity-60"
                    data-testid="listing-action-copy"
                >
                    <Copy className="w-4 h-4" /> Kopieren
                </button>
            </div>

            {archiveOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="archive-modal">
                    <div className="bg-white rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 font-display font-black text-lg">
                                <Archive className="w-5 h-5 text-gray-800" /> Inserat archivieren
                            </div>
                            <button onClick={() => setArchiveOpen(false)} className="p-1 text-gray-400 hover:text-gray-700" data-testid="archive-modal-close"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 mb-3 flex gap-2" data-testid="archive-reasons">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-[12px] text-amber-900">
                                Löschen nicht möglich, da {archiveReasons.length ? archiveReasons.join(" sowie ") : "geschützte Daten vorhanden sind"}. Das Inserat kann nur archiviert werden. Es bleibt in der Suche sichtbar (Badge „Archivierung") und kann <strong>nicht</strong> erneut online gestellt werden.
                            </div>
                        </div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Archivierungsgrund *</label>
                        <textarea
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                            rows={3}
                            placeholder="z. B. Fahrzeug verkauft, Dokumentation zur Aufbewahrung"
                            className="w-full mt-1 border border-gray-300 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                            data-testid="archive-reason-input"
                        />
                        <div className="flex flex-wrap justify-between items-center gap-2 mt-4">
                            <button onClick={submitForceDelete} disabled={busy === "force-delete"}
                                className="px-3 h-10 rounded-sm border-2 border-red-500 text-red-700 text-[12px] font-bold hover:bg-red-50 disabled:opacity-60"
                                title="Nur Filialleitung/Hauptsitz: entfernt das Fahrzeug endgültig trotz Verknüpfungen (Protokoll bleibt 2 Jahre)"
                                data-testid="archive-force-delete-btn">
                                {busy === "force-delete" ? "Entferne…" : "Endgültig löschen (Filialleitung)"}
                            </button>
                            <div className="flex gap-2">
                                <button onClick={() => setArchiveOpen(false)} className="px-4 h-10 rounded-sm border border-gray-300 text-sm font-bold" data-testid="archive-cancel-btn">Abbrechen</button>
                                <button onClick={submitArchive} disabled={busy === "archive" || !archiveReason.trim()} className="px-4 h-10 rounded-sm bg-gray-800 hover:bg-black text-white text-sm font-bold disabled:opacity-60" data-testid="archive-confirm-btn">
                                    {busy === "archive" ? "Archiviere…" : "Archivieren"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {pledgeOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="pledge-modal">
                    <div className="bg-white rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2 font-display font-black text-lg">
                                <Landmark className="w-5 h-5 text-violet-700" /> Pfand / Refinanzieren
                            </div>
                            <button onClick={() => setPledgeOpen(false)} className="p-1 text-gray-400 hover:text-gray-700" data-testid="pledge-modal-close"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <button type="button" onClick={() => setPledge((p) => ({ ...p, kind: "pfand" }))} className={`h-10 rounded-sm text-sm font-bold border-2 ${pledge.kind === "pfand" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-gray-200 text-gray-600"}`} data-testid="pledge-kind-pfand">Pfand (Pfandhaus)</button>
                            <button type="button" onClick={() => setPledge((p) => ({ ...p, kind: "refinanzierung" }))} className={`h-10 rounded-sm text-sm font-bold border-2 ${pledge.kind === "refinanzierung" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-gray-200 text-gray-600"}`} data-testid="pledge-kind-refinanzierung">Refinanzierung (Bank)</button>
                        </div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-600">{pledge.kind === "refinanzierung" ? "Finanzierende Bank" : "Pfandhaus / Partner"}</label>
                        <input value={pledge.partner} onChange={(e) => setPledge((p) => ({ ...p, partner: e.target.value }))} placeholder={pledge.kind === "refinanzierung" ? "z. B. BMW Bank GmbH" : "z. B. Pfandhaus München"} className="w-full mt-1 mb-3 border border-gray-300 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" data-testid="pledge-partner-input" />
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Betrag (€)</label>
                                <input type="number" min="0" value={pledge.amount} onChange={(e) => setPledge((p) => ({ ...p, amount: e.target.value }))} placeholder="0" className="w-full mt-1 border border-gray-300 rounded-sm p-2.5 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-violet-400" data-testid="pledge-amount-input" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Datum</label>
                                <input type="date" value={pledge.date} onChange={(e) => setPledge((p) => ({ ...p, date: e.target.value }))} className="w-full mt-1 border border-gray-300 rounded-sm p-2.5 text-sm font-mono-num focus:outline-none focus:ring-2 focus:ring-violet-400" data-testid="pledge-date-input" />
                            </div>
                        </div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Notiz</label>
                        <textarea value={pledge.note} onChange={(e) => setPledge((p) => ({ ...p, note: e.target.value }))} rows={2} placeholder="Zusätzliche Vermerke …" className="w-full mt-1 mb-3 border border-gray-300 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" data-testid="pledge-note-input" />
                        <label className="flex items-center gap-2 cursor-pointer text-sm mb-4">
                            <input type="checkbox" checked={pledge.active} onChange={(e) => setPledge((p) => ({ ...p, active: e.target.checked }))} className="w-4 h-4 accent-violet-600" data-testid="pledge-active-toggle" />
                            <span className="font-bold">Aktiv — Fahrzeug ist aktuell verpfändet / refinanziert</span>
                        </label>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setPledgeOpen(false)} className="px-4 h-10 rounded-sm border border-gray-300 text-sm font-bold" data-testid="pledge-cancel-btn">Abbrechen</button>
                            <button onClick={savePledge} disabled={busy === "pledge"} className="px-4 h-10 rounded-sm bg-violet-700 hover:bg-violet-800 text-white text-sm font-bold disabled:opacity-60" data-testid="pledge-save-btn">
                                {busy === "pledge" ? "Speichere…" : "Speichern"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
